import { useRef, useLayoutEffect, useState } from "react";

export const AutosizeText = ({
  children,
  original: Component = "span",
  style,
  min = 16,
  max = 32,
  ...props
}) => {
  const ref = useRef(null);
  const rafRef = useRef(null);
  const [state, setState] = useState({ fontSize: max, overflow: false });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;

    const recalc = () => {
      const siblings = [...parent.children].filter((child) => child !== el);
      const siblingsWidth = siblings.reduce((sum, s) => sum + s.offsetWidth, 0);
      const parentStyle = getComputedStyle(parent);
      const gap = parseFloat(parentStyle.gap) || 0;
      const padding =
        parseFloat(parentStyle.paddingLeft) +
        parseFloat(parentStyle.paddingRight);

      const availableWidth =
        parent.clientWidth - siblingsWidth - gap * siblings.length - padding;
      if (availableWidth <= 0) return;

      // 측정 중 깜빡임 방지
      const prevVisibility = el.style.visibility;
      el.style.visibility = "hidden";
      el.style.whiteSpace = "nowrap";
      el.style.wordBreak = "normal";
      el.style.fontSize = `${max}px`;

      if (el.scrollWidth <= availableWidth) {
        el.style.visibility = prevVisibility;
        setState({ fontSize: max, overflow: false });
        return;
      }

      let lo = min,
        hi = max;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        el.style.fontSize = `${mid}px`;
        if (el.scrollWidth > availableWidth) hi = mid - 1;
        else lo = mid + 1;
      }

      const finalSize = Math.max(hi, min);
      el.style.fontSize = `${finalSize}px`;
      const isOverflow = el.scrollWidth > availableWidth;

      el.style.visibility = prevVisibility;
      setState({ fontSize: finalSize, overflow: isOverflow });
    };

    const scheduleRecalc = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(recalc);
    };

    const observer = new ResizeObserver(scheduleRecalc);
    observer.observe(parent);
    scheduleRecalc();

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [children, min, max]);

  return (
    <Component
      ref={ref}
      style={{
        fontSize: `${state.fontSize}px`,
        whiteSpace: state.overflow ? "normal" : "nowrap",
        wordBreak: state.overflow ? "break-word" : "normal",
        display: "block",
        ...style,
      }}
      {...props}>
      {children}
    </Component>
  );
};
